import { PropertyValueSet } from "@genesys/property";
import { PropertyDefinitions, loadRuleToPropertyDefinition } from "../../tools";
import { TopLevelOverrides } from "../";
import * as Authorization from "@genesys/shared/lib/authorization";
import * as SharedState from "../../../shared-state";
import * as GraphQLTypes from "../../../graphql-types";
import * as BuildingSettings from "../../tools/moisture-load-rules/building-settings";
import * as InfiltrationModelSettings from "../../tools/moisture-load-rules/infiltration-model-settings";
import * as RoomControlSettings from "../../tools/moisture-load-rules/room-control-settings";
import * as SystemSettings from "../../tools/moisture-load-rules/system-settings";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import { LoadDef, loadMapCreator } from "../../main-content/load-types";
import { MoistureHeatLoadTypeEnum } from "@genesys/graphql-types";

export function getPropertyDefinitions(
  moistureLoadRules: GraphQLTypes.MoistureLoadRulesType,
  translate: LanguageTexts.Translate
): PropertyDefinitions {
  return moistureLoadRules.propertyDefinitions.map((x, index) =>
    loadRuleToPropertyDefinition(x, index, translate)
  );
}

export function createLoadDefinitions(moistureLoadClaimValue: number) {
  return (
    Object.keys(MoistureHeatLoadTypeEnum) as Array<
      keyof typeof MoistureHeatLoadTypeEnum
    >
  ).map(key => {
    const loadCreator = loadMapCreator[key];
    if (!loadCreator) {
      throw new Error(`No load creator found for key: ${key}`);
    }
    return loadCreator(moistureLoadClaimValue);
  });
}

export function extractTopLevelOverrides(
  propertiesToKeep: string[],
  allLoadProperties: string[],
  allOverridableLoadProperties: string[]
) {
  const topLevelProperties = propertiesToKeep
    .filter(x => !allOverridableLoadProperties.includes(x))
    .filter(x => !allLoadProperties.includes(x));

  const topLevelOverridableProperties: ReadonlyArray<TopLevelOverrides> = [
    {
      useProperty: BuildingSettings.manualInputProperty,
      overridableProperties: BuildingSettings.overridableProperties
    },
    {
      useProperty: InfiltrationModelSettings.manualInputProperty,
      overridableProperties: InfiltrationModelSettings.overridableProperties
    },
    {
      useProperty: RoomControlSettings.manualInputProperty,
      overridableProperties: RoomControlSettings.overridableProperties
    },
    {
      useProperty: SystemSettings.manualInputProperty,
      overridableProperties: SystemSettings.overridableProperties
    }
  ];

  return { topLevelProperties, topLevelOverridableProperties };
}

export function extractUserSettings(
  userQuery: GraphQLTypes.MoistureLoadCalculationUserQuery
) {
  const moistureLoadUserSettings =
    userQuery.user.userProfile.userSettings.moistureLoadToolUserSettings;

  const climateDataFromMlSettings = PropertyValueSet.fromString(
    moistureLoadUserSettings?.climateSettings || ""
  );

  return { moistureLoadUserSettings, climateDataFromMlSettings };
}

export function getHighestMoistureLoadClaim(
  sharedState: SharedState.State
): number {
  const claimValues = Authorization.getClaimValues(
    sharedState.user.applicationClaims,
    Authorization.genesysUserClaims.moistureLoadCalculation
  ) || ["1"];

  return claimValues.map(cv => parseInt(cv, 10)).sort((a, b) => b - a)[0]; // Reverse sorting to get the highest value
}

export function extractLoadProperties(allLoads: LoadDef[]) {
  return {
    allLoadProperties: allLoads.flatMap(x => x.properties.loadProperties),
    allOverridableLoadProperties: allLoads.flatMap(
      x => x.properties.overridableProperties
    )
  };
}
